/**
 * The tail end of a successful sign-in, shared by every channel.
 *
 * Password login, email OTP and Google all produce the same `{ accessToken,
 * userData }` payload, so what happens next should not depend on how the person
 * got here: store the session, push any cart built while they were anonymous up
 * to the server, then land them on the home page. This used to live inline in
 * PageLogin; it is a hook now so the OTP and Google paths cannot drift from it.
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppDispatch, RootState } from 'reduxStore/store';
import { handleLogin } from '../reduxStore/store/auth';
import { bulkCreate, clearCartList, getCart } from '../reduxStore/store/cart';
import { AuthSessionResponse } from '../service/authApi';

export interface EstablishSessionOptions {
    /**
     * Where to land afterwards. Left alone, every fresh session goes home.
     */
    redirectTo?: string;
}

/**
 * Where a fresh session lands.
 *
 * Always home. Card activation is an APP-ONLY step and deliberately has no web
 * equivalent: the card is a physical object that arrives by post, and linking it
 * is the one thing the customer must do on the handset they will actually carry.
 *
 * This used to honour `nextStep: "card_activation"` from the server and send a
 * new signup to a `/activate-card` page on the site. That page let them skip,
 * which made a mandatory step optional for anyone who happened to sign up on the
 * web, and it split one flow across two products. The server still sends the
 * field -- the app reads it -- the website simply no longer acts on it.
 */
const landingFor = (_session: AuthSessionResponse): string => '/';

/**
 * Where PublicRoute should send a visitor who has just signed in.
 *
 * /login and /signup are wrapped in PublicRoute, which bounces any logged-in
 * visitor to "/". The moment handleLogin lands in the store, that wrapper is
 * still mounted for the page we are leaving, so it re-renders, emits
 * <Navigate to="/"> and -- because <Navigate> pushes from a mount effect --
 * wins the race against the navigate() below. A brand new customer was landing
 * on the home page instead of card activation, with nothing in the logs to
 * explain it.
 *
 * Parking the intended destination here makes the two agree: whichever
 * navigation happens to win, both send the visitor to the same place.
 */
export const PENDING_REDIRECT_KEY = 'instaacard:postAuthRedirect';

const rememberLanding = (path: string) => {
    try {
        // Stamped, and deliberately NOT cleared by this hook: PublicRoute's
        // <Navigate> fires from a mount effect that runs after this callback
        // finishes, so clearing it here would leave that redirect with nothing
        // to read. Whichever side consumes it removes it; the timestamp keeps a
        // leftover entry from misrouting a later sign-in.
        if (path && path !== '/') sessionStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ path, ts: Date.now() }));
    } catch (err) {
        // Private mode / blocked storage: the redirect is a nicety, not a
        // correctness requirement, so fall through to the normal landing.
    }
};

export const useAuthSession = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const localCart = useSelector((state: RootState) => state?.CartSlice?.cart);

    return useCallback(
        async (session: AuthSessionResponse, options: EstablishSessionOptions = {}) => {
            // Park the destination BEFORE the store update, because that update
            // is what wakes PublicRoute's redirect. See rememberLanding above.
            const landing = options.redirectTo ?? landingFor(session);
            rememberLanding(landing);

            // Writes the token to localStorage synchronously, which is what the
            // axios request interceptor reads - so it has to happen before any
            // authenticated call below.
            dispatch(handleLogin(session));

            const cartData = localCart?.map((item: any) => {
                return {
                    productId: item?.ProductMaster?.id,
                    quantity: item?.quantity,
                    name: item?.itemData?.name,
                    company: item?.itemData?.company,
                    designation: item?.itemData?.designation,
                    mobile: item?.itemData?.mobile,
                    companyLogo: item?.itemData?.companyLogo,
                    designFile: item?.itemData?.designFile,
                    variant: item?.itemData?.variant
                };
            });

            try {
                if (cartData && cartData.length > 0) {
                    await dispatch(bulkCreate({ cartData: cartData })).unwrap();
                }
            } catch (err) {
                // A cart that failed to merge is not a reason to block the
                // sign-in; refreshing the cart below leaves them consistent.
            }

            dispatch(clearCartList());
            dispatch(getCart());

            navigate(landing, { replace: true });
        },
        [dispatch, localCart, navigate]
    );
};

export default useAuthSession;
